import React, { useState, useCallback, useMemo } from 'react';
import { Row, Col, Card, CardBody, Container, Spinner } from 'reactstrap';

import * as subscriptionsActions from '../../store/subscriptions/actions';
import * as usersActions from '../../store/users/actions';
import { useDispatch, useSelector } from 'react-redux';
import { selectUser } from '../../selectors/user';
import {
  totalSubscriptions as totalSubscriptionsSelector,
  totalRevenues as totalRevenuesSelector,
} from '../../selectors/subscription.js';

import Modal from '../../components/Common/Modal/Modal';
import ShowSingleElement from '../../components/Common/ShowSingleElement';
import Breadcrumbs from '../../components/Common/Breadcrumb';
import DateRangeFilter from '../../components/Common/DateRangeFilter/DateRangeFilter';
import DataTableNext from '../../components/Common/DataTable/DataTableNext';
import createTableColumns from '../../components/Subscriptions/tableColumns';
import {
  ExportButtonPlacement,
  ToggleColumnButton,
} from '../../components/Common/DataTable/tableActionButtons';
import { addOrRemove } from '../../helpers/sharedFunction';
import MiniCard from '../../components/Common/MiniCard';

import moment from 'moment';

const List = (props) => {
  const [viewModal, setViewModal] = useState(false);
  const [selectedSubscription, setSelectedSubscription] = useState(null);
  const [userModal, setUserModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  const [filters, setFilters] = useState([]);
  const [sorting, setSorting] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);

  const loading = useSelector((state) => state.Subscription.loading);

  const subscriptions = useSelector(
    (state) => state.Subscription.subscriptions,
  );
  const showSelectedUser = useSelector(selectUser(selectedUser?.id));

  //Metrics
  const totalSubscriptions = useSelector(totalSubscriptionsSelector);
  const totalRevenues = useSelector(totalRevenuesSelector);

  const startDate = useSelector((state) => state.Subscription.startDate);
  const endDate = useSelector((state) => state.Subscription.endDate);
  const momentStartDate = startDate ? moment(startDate) : null;
  const momentEndDate = endDate ? moment(endDate) : null;

  const [selectedFilters, setSelectedFilters] = useState({
    startDate: momentStartDate || moment.utc().startOf('day'),
    endDate: momentEndDate || moment.utc(),
  });

  const dispatch = useDispatch();

  const refresh = useCallback(({ startDate, endDate }) => {
    const selectedStartDate = startDate.utc().startOf('day').toDate();
    const selectedEndDate = endDate.utc().endOf('day').toDate();
    dispatch(
      subscriptionsActions.fetchSubscriptions(
        selectedStartDate,
        selectedEndDate,
      ),
    );
    setSelectedFilters({ startDate, endDate });
  }, []);

  const openUserModal = (subscription) => {
    setSelectedUser(subscription.userId);
    dispatch(usersActions.fetchUser(subscription.userId));
    setUserModal(true);
  };

  const openViewModal = (subscription) => {
    setSelectedSubscription(subscription);
    setViewModal(true);
  };

  const columns = useMemo(
    () => createTableColumns(openUserModal, openViewModal),
    [],
  );
  const [hiddenColumns, setHiddenColumns] = useState(
    columns.filter(({ hidden }) => !!hidden).map(({ name }) => name),
  );
  const handleToggleColumn = (name) => {
    setHiddenColumns(addOrRemove(hiddenColumns, name));
  };

  const metrics = [
    {
      title: 'Totale abbonamenti',
      text: totalSubscriptions,
      color: 'info',
      icon: 'fas fa-credit-card',
    },
    {
      title: 'Totale ricavi',
      text: `${isFinite(totalRevenues) ? totalRevenues.toFixed(2) : 0} €`,
      color: 'dark',
      icon: 'fas fa-money-bill',
    },
  ];

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid={true}>
          <Breadcrumbs
            title="Subscriptions"
            onRefresh={() => refresh(selectedFilters)}
            loading={loading}
          />
          <DateRangeFilter
            onUpdate={refresh}
            startDate={selectedFilters.startDate}
            endDate={selectedFilters.endDate}
            loading={loading}
            showFilters
            initialFilters={selectedFilters}
          />
          <Row>
            {metrics.map((metric, index) => (
              <Col key={'metric_' + index}>
                <MiniCard {...metric} loading={loading} />
              </Col>
            ))}
          </Row>
          <Row>
            <Col className="col-12">
              <Card>
                <CardBody>
                  <Row className="align-items-center justify-content-end mb-2">
                    <Spinner
                      color="secondary"
                      animation="border"
                      hidden={!loading}
                    />
                    <ToggleColumnButton
                      columns={columns}
                      hiddenColumns={hiddenColumns}
                      onToggleColumn={handleToggleColumn}
                    />
                    <ExportButtonPlacement />
                  </Row>
                  <DataTableNext
                    loading={loading}
                    rows={subscriptions}
                    columns={columns}
                    filters={filters}
                    onFiltersChange={setFilters}
                    sorting={sorting}
                    onSortingChange={setSorting}
                    currentPage={currentPage}
                    onCurrentPageChange={setCurrentPage}
                    hiddenColumns={hiddenColumns}
                    onHiddenColumnsChange={setHiddenColumns}
                    pageSize={pageSize}
                    onPageSizeChange={setPageSize}
                  />
                </CardBody>
              </Card>
            </Col>
          </Row>
          <Modal
            size="xl"
            title="User"
            isOpen={userModal}
            toggle={() => setUserModal(!userModal)}
            showClose
            scrollable={false}>
            <ShowSingleElement
              element={showSelectedUser}
              collection="users"
              icon="bx bx-user-pin"
            />
          </Modal>
          <Modal
            size="lg"
            title="Subscription"
            isOpen={viewModal}
            toggle={() => setViewModal(!viewModal)}
            showClose
            scrollable={false}>
            <ShowSingleElement
              element={selectedSubscription}
              collection="subscriptions"
              icon="bx bx-paper-plane"
            />
          </Modal>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default List;
